import { Icon,IconProps } from '@toggle/design-system';
import { formatPercentage, formatPriceWithCurrency } from '@toggle/helpers';
import { EarningsInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';

import * as S from './EarningsInsightTooltip.styles';

interface EarningsInsightTooltipProps {
  earningsInsight: EarningsInsight;
  isPreview: boolean;
  iconProps: IconProps;
}

export const EarningsInsightTooltip = ({
  isPreview,
  earningsInsight,
  iconProps,
}: EarningsInsightTooltipProps) => {
  const { t } = useTranslation(['earnings']);
  const { data: earningsData } = useGetEarningById(earningsInsight.earnings_id);

  const title = isPreview
    ? t('earnings:earningsInsight.drawer.earningsPreview.title')
    : t('earnings:earningsInsight.drawer.earningsRecap.title');
  const epsLabel = isPreview
    ? t('earnings:earningsInsight.drawer.epsExpectations')
    : t('earnings:earningsInsight.drawer.epsActual');
  const epsValue = isPreview
    ? earningsData?.eps?.estimate
    : earningsData?.eps?.value;
  const deltaValue = earningsData?.eps?.surprise_percentage;

  const isNegativeDelta = !!deltaValue && deltaValue < 0;

  return (
    <S.TooltipWrapper>
      <Icon
        iconName={iconProps.iconName}
        viewBox={iconProps.viewBox}
        size={32}
      />
      <S.RightWrapper>
        <S.TitleLabel>{title}</S.TitleLabel>
        <S.BottomWrapper>
          {epsValue !== undefined && (
            <>
              <S.DescriptionLabel>{epsLabel}</S.DescriptionLabel>
              <S.ValueWrapper>
                {formatPriceWithCurrency(epsValue, earningsData?.currency)}
              </S.ValueWrapper>
            </>
          )}
          {!isPreview && !!deltaValue && (
            <S.RecapWrapper>
              <S.RecapValue $isNegative={isNegativeDelta}>
                {formatPercentage(deltaValue, {
                  suffix: '%',
                  addPlus: !isNegativeDelta,
                  useOriginalValue: true,
                })}
              </S.RecapValue>
              <S.RecapLabel>
                {t('earnings:earningsInsight.drawer.vsConsExp')}
              </S.RecapLabel>
            </S.RecapWrapper>
          )}
        </S.BottomWrapper>
      </S.RightWrapper>
    </S.TooltipWrapper>
  );
};
